import { z } from 'zod'
import { type FailureCode, KbSourceKind, KbSourceStatus } from '../enums'
import { Id, Instant, Ratio } from './primitives'
import { GroundingSource } from './suggestions'

export const KB_TITLE_MAX_CHARS = 200
export const KB_URI_MAX_CHARS = 2048
export const KB_QUESTION_MAX_CHARS = 2000
export const KB_LIST_DEFAULT_LIMIT = 20
export const KB_LIST_MAX_LIMIT = 100
export const KB_MAX_PAGE_NUMBER = 10_000
export const KB_UPLOAD_MAX_BYTES = 10 * 1024 * 1024
export const KB_UPLOAD_CONTENT_TYPES = ['text/plain', 'text/markdown', 'text/html', 'application/pdf'] as const
export type KbUploadContentType = (typeof KB_UPLOAD_CONTENT_TYPES)[number]
export const KB_UPLOAD_EXTENSIONS: Record<KbUploadContentType, string> = {
  'text/plain': 'txt',
  'text/markdown': 'md',
  'text/html': 'html',
  'application/pdf': 'pdf',
}

const HTTP_URL_PROTOCOL = /^https?$/

export const KbBudgetShortfall = z.object({
  neededTokens: z.number().int().nonnegative(),
  remainingTokens: z.number().int().nonnegative(),
})
export type KbBudgetShortfall = z.infer<typeof KbBudgetShortfall>

export const KbSource = z.object({
  id: Id,
  kind: KbSourceKind,
  title: z.string(),
  uri: z.string().nullable(),
  status: KbSourceStatus,
  version: z.number().int().positive(),
  error: z.string().nullable(),
  budgetShortfall: KbBudgetShortfall.nullable(),
  chunkCount: z.number().int().nonnegative(),
  updatedAt: Instant,
})
export type KbSource = z.infer<typeof KbSource>

export const KB_SETTLING_STATUSES = ['pending', 'ingesting'] as const
export type KbSettlingStatus = (typeof KB_SETTLING_STATUSES)[number]
type KbSettledStatus = Exclude<KbSourceStatus, KbSettlingStatus>

const KB_RETRYING_STATUS: KbSettlingStatus = 'pending'
const KB_DEAD_LETTERED_STATUS: KbSettledStatus = 'failed'

export const kbIsSettling = (status: KbSourceStatus): boolean =>
  KB_SETTLING_STATUSES.some((settling) => settling === status)

export const kbStatusAfterFailure = (permanent: boolean): KbSourceStatus =>
  permanent ? KB_DEAD_LETTERED_STATUS : KB_RETRYING_STATUS

export const KB_RETRYABLE_FAILURES = [
  'provider_unavailable',
  'provider_timeout',
  'provider_rate_limited',
  'kb_source_parse_incomplete',
  'budget_exhausted',
  'credentials_missing',
] as const satisfies readonly FailureCode[]

const isRetryableFailure = (error: string | null): boolean =>
  KB_RETRYABLE_FAILURES.some((retryable) => retryable === error)

export const kbCanRetry = (source: { status: KbSourceStatus; error: string | null }): boolean =>
  source.status === KB_DEAD_LETTERED_STATUS && isRetryableFailure(source.error)

export const KbTestResult = z.object({
  answerable: z.boolean(),
  topSimilarity: Ratio,
  threshold: Ratio,
  sources: z.array(GroundingSource),
})
export type KbTestResult = z.infer<typeof KbTestResult>

const Title = z.string().trim().min(1).max(KB_TITLE_MAX_CHARS)

export const CreateKbSourceRequest = z.strictObject({
  kind: z.literal('url'),
  title: Title,
  uri: z.url({ protocol: HTTP_URL_PROTOCOL }).max(KB_URI_MAX_CHARS),
})
export type CreateKbSourceRequest = z.infer<typeof CreateKbSourceRequest>

export const UploadKbSourceForm = z.object({
  title: Title,
  contentType: z.enum(KB_UPLOAD_CONTENT_TYPES),
  sizeBytes: z.number().int().positive().max(KB_UPLOAD_MAX_BYTES),
})
export type UploadKbSourceForm = z.infer<typeof UploadKbSourceForm>

export const UpdateKbSourceRequest = z
  .strictObject({
    title: Title.optional(),
    uri: z.url({ protocol: HTTP_URL_PROTOCOL }).max(KB_URI_MAX_CHARS).optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: 'at least one field is required' })
export type UpdateKbSourceRequest = z.infer<typeof UpdateKbSourceRequest>

export const KbSourceListQuery = z.strictObject({
  page: z.coerce.number().int().min(1).max(KB_MAX_PAGE_NUMBER).default(1),
  limit: z.coerce.number().int().positive().max(KB_LIST_MAX_LIMIT).default(KB_LIST_DEFAULT_LIMIT),
})
export type KbSourceListQuery = z.infer<typeof KbSourceListQuery>

export const KbTestRequest = z.strictObject({ question: z.string().trim().min(1).max(KB_QUESTION_MAX_CHARS) })
export type KbTestRequest = z.infer<typeof KbTestRequest>
